import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { TabPanel, TabView } from "primereact/tabview";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { GetBookingInvoicesURL, GetBookingRefundsURL } from "../../../../utils/urls";
import Layout from "../../component/layout";
import BookingInvoiceDialog from "./BookingInvoiceDialog";
import RecordPaymentDialog from "./RecordPaymentDialog";

const BookingInvoices = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [invoiceDialogVisible, setInvoiceDialogVisible] = useState(false);
    const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
    const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [refunds, setRefunds] = useState([]);
    const [filteredRefunds, setFilteredRefunds] = useState([]);
    const [refundSearchTerm, setRefundSearchTerm] = useState("");
    const [refundStatusFilter, setRefundStatusFilter] = useState("");
    const [summary, setSummary] = useState({
        totalInvoices: 0,
        overdueCount: 0,
        byCurrency: {}
    });

    const fetchInvoices = useCallback(async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            if (!facilityId) {
                toastify("No facility selected", "error");
                return;
            }

            const response = await makeRequest2(`${GetBookingInvoicesURL}/${facilityId}`, "GET");

            if (response?.success && response.data && response.data.data) {
                const { invoices: fetchedInvoices, summary: fetchedSummary } = response.data.data;

                if (Array.isArray(fetchedInvoices)) {
                    setInvoices(fetchedInvoices);
                    setFilteredInvoices(fetchedInvoices);
                    setSummary(fetchedSummary || {
                        totalInvoices: 0,
                        overdueCount: 0,
                        byCurrency: {}
                    });
                } else {
                    throw new Error('Invalid data format received');
                }
            } else {
                throw new Error(response?.error || 'Failed to fetch invoices');
            }
        } catch (error) {
            toastify(error.message || "Error fetching invoices", "error");
            setInvoices([]);
            setFilteredInvoices([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRefunds = useCallback(async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            if (!facilityId) {
                toastify("No facility selected", "error");
                return;
            }

            const response = await makeRequest2(`${GetBookingRefundsURL}/${facilityId}`, "GET");

            if (response?.success && response.data?.data) {
                const fetchedRefunds = response.data.data.refunds || [];
                setRefunds(fetchedRefunds);
                setFilteredRefunds(fetchedRefunds);
            } else {
                throw new Error(response?.error || 'Failed to fetch refunds');
            }
        } catch (error) {
            toastify(error.message || "Error fetching refunds", "error");
            setRefunds([]);
            setFilteredRefunds([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = (value) => {
        setSearchTerm(value);
        filterInvoices(value, selectedStatus, selectedPaymentStatus);
    };

    const handleStatusChange = (status) => {
        setSelectedStatus(status);
        filterInvoices(searchTerm, status, selectedPaymentStatus);
    };

    const handlePaymentStatusChange = (paymentStatus) => {
        setSelectedPaymentStatus(paymentStatus);
        filterInvoices(searchTerm, selectedStatus, paymentStatus);
    };

    const handleRefundSearch = (value) => {
        setRefundSearchTerm(value);
        filterRefunds(value, refundStatusFilter);
    };

    const handleRefundStatusChange = (status) => {
        setRefundStatusFilter(status);
        filterRefunds(refundSearchTerm, status);
    };

    const handlePaymentSuccess = useCallback(async () => {
        // Refresh the invoices list after successful payment
        await fetchInvoices();
        setPaymentDialogVisible(false);
        setSelectedInvoiceForPayment(null);
    }, [fetchInvoices]);

    const filterInvoices = (search, status, paymentStatus) => {
        let filtered = [...invoices];

        if (search) {
            filtered = filtered.filter(invoice =>
                invoice.guestInfo?.name?.toLowerCase().includes(search.toLowerCase()) ||
                invoice.reservationDetails?.bookingReservationId?.toLowerCase().includes(search.toLowerCase()) ||
                invoice.invoiceNumber?.toLowerCase().includes(search.toLowerCase()) ||
                invoice.invoiceId?.toLowerCase().includes(search.toLowerCase()) ||
                invoice.unitDetails?.name?.toLowerCase().includes(search.toLowerCase())
            );
        }

        if (status) {
            filtered = filtered.filter(invoice => invoice.status === status);
        }

        if (paymentStatus) {
            filtered = filtered.filter(invoice => invoice.paymentStatus === paymentStatus);
        }

        setFilteredInvoices(filtered);
    };

    const filterRefunds = (search, status) => {
        let filtered = [...refunds];

        if (search) {
            filtered = filtered.filter(refund =>
                refund.guestName?.toLowerCase().includes(search.toLowerCase()) ||
                refund.bookingReservationId?.toLowerCase().includes(search.toLowerCase()) ||
                refund.refundReference?.toLowerCase().includes(search.toLowerCase()) ||
                refund.unitName?.toLowerCase().includes(search.toLowerCase())
            );
        }

        if (status) {
            filtered = filtered.filter(refund => refund.refundStatus === status);
        }

        setFilteredRefunds(filtered);
    };

    const statusTemplate = (rowData) => {
        const statusStyles = {
            active: { color: '#28a745', backgroundColor: '#e9fbe9' },
            cancelled: { color: '#dc3545', backgroundColor: '#fde9e9' },
            void: { color: '#6c757d', backgroundColor: '#f1f1f1' }
        };

        const style = statusStyles[rowData.status] || {
            color: '#6c757d',
            backgroundColor: '#f1f1f1',
        };

        return (
            <span
                style={{
                    ...style,
                    padding: '0.5em 1em',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'inline-block',
                    textTransform: 'capitalize'
                }}
            >
                {rowData.status}
            </span>
        );
    };

    const paymentStatusTemplate = (rowData) => {
        const paymentStatusStyles = {
            Pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
            Paid: { color: '#28a745', backgroundColor: '#e9fbe9' },
            'Partially Paid': { color: '#fd7e14', backgroundColor: '#fff4e6' },
            Refunded: { color: '#17a2b8', backgroundColor: '#e1f7fa' },
            Cancelled: { color: '#dc3545', backgroundColor: '#fde9e9' }
        };

        const style = paymentStatusStyles[rowData.paymentStatus] || {
            color: '#6c757d',
            backgroundColor: '#f1f1f1',
        };

        const isOverdue = rowData.calculatedData?.isOverdue;

        return (
            <div className="d-flex flex-column">
                <span
                    style={{
                        ...style,
                        padding: '0.5em 1em',
                        borderRadius: '12px',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        display: 'inline-block',
                        marginBottom: isOverdue ? '0.25rem' : '0'
                    }}
                >
                    {rowData.paymentStatus}
                </span>
                {isOverdue && (
                    <small className="text-danger fw-bold">OVERDUE</small>
                )}
            </div>
        );
    };

    const refundStatusTemplate = (rowData) => {
        const styles = {
            Pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
            Approved: { color: '#28a745', backgroundColor: '#e9fbe9' },
            Rejected: { color: '#dc3545', backgroundColor: '#fde9e9' }
        };

        const style = styles[rowData.refundStatus] || {
            color: '#6c757d',
            backgroundColor: '#f1f1f1',
        };

        return (
            <span
                style={{
                    ...style,
                    padding: '0.5em 1em',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'inline-block'
                }}
            >
                {rowData.refundStatus || 'Pending'}
            </span>
        );
    };

    const refundAmountTemplate = (rowData) => {
        const currencyCode = rowData.currencyCode || 'USD';
        return (
            <div className="text-end">
                <strong>{currencyCode} {Number(rowData.refundAmount || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}</strong>
            </div>
        );
    };

    const amountTemplate = (rowData) => {
        const currencyCode = rowData.currencyDetails?.code || 'USD';
        const amount = rowData.totalAmount || 0;

        return (
            <div className="text-end">
                <strong>{currencyCode} {amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}</strong>
            </div>
        );
    };

    const balanceTemplate = (rowData) => {
        const currencyCode = rowData.currencyDetails?.code || 'USD';
        const balance = rowData.calculatedData?.balanceRemaining || 0;
        const isOverdue = rowData.calculatedData?.isOverdue;

        return (
            <div className={`text-end ${balance > 0 ? (isOverdue ? 'text-danger' : 'text-warning') : 'text-success'}`}>
                <strong>{currencyCode} {balance.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}</strong>
            </div>
        );
    };

    const durationTemplate = (rowData) => {
        const nights = rowData.calculatedData?.nights || 0;
        return `${nights} ${nights === 1 ? 'night' : 'nights'}`;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info"
                    onClick={() => {
                        setSelectedInvoiceId(rowData._id);
                        setInvoiceDialogVisible(true);
                    }}
                    tooltip="View Invoice"
                />
                {rowData.paymentStatus !== 'Paid' && rowData.paymentStatus !== 'Cancelled' && (
                    <Button
                        icon="ti ti-credit-card"
                        className="p-button-rounded p-button-success"
                        onClick={() => {
                            setSelectedInvoiceForPayment(rowData);
                            setPaymentDialogVisible(true);
                        }}
                        tooltip="Record Payment"
                    />
                )}
            </div>
        );
    };

    useEffect(() => {
        fetchInvoices();
        fetchRefunds();
    }, [fetchInvoices, fetchRefunds]);

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/facility/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="">Booking Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="">Invoices</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="card bg-primary text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="ti ti-file-invoice" style={{ fontSize: '2rem' }}></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0">Total Invoices</h6>
                                    <h4 className="mb-0">{summary.totalInvoices}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card bg-success text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="ti ti-currency-dollar" style={{ fontSize: '2rem' }}></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0">Total Amount</h6>
                                    {Object.entries(summary.byCurrency || {}).map(([currency, data]) => (
                                        <div key={currency} className="mb-1">
                                            <h5 className="mb-0">{currency} {data.totalAmount.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}</h5>
                                        </div>
                                    ))}
                                    {Object.keys(summary.byCurrency || {}).length === 0 && (
                                        <h4 className="mb-0">0.00</h4>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card bg-info text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="ti ti-check" style={{ fontSize: '2rem' }}></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0">Total Paid</h6>
                                    {Object.entries(summary.byCurrency || {}).map(([currency, data]) => (
                                        <div key={currency} className="mb-1">
                                            <h5 className="mb-0">{currency} {data.totalPaid.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}</h5>
                                        </div>
                                    ))}
                                    {Object.keys(summary.byCurrency || {}).length === 0 && (
                                        <h4 className="mb-0">0.00</h4>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card bg-warning text-white">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <i className="ti ti-alert-triangle" style={{ fontSize: '2rem' }}></i>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-0">Overdue</h6>
                                    <h4 className="mb-0">{summary.overdueCount}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <h5>Booking Invoices</h5>
                        </div>
                        <div className="card-body">
                            <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                                <TabPanel header="Invoices">
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <input
                                                className="form-control"
                                                placeholder="Search invoices..."
                                                value={searchTerm}
                                                onChange={(e) => handleSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <select
                                                className="form-control"
                                                value={selectedStatus}
                                                onChange={(e) => handleStatusChange(e.target.value)}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="active">Active</option>
                                                <option value="cancelled">Cancelled</option>
                                                <option value="void">Void</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <select
                                                className="form-control"
                                                value={selectedPaymentStatus}
                                                onChange={(e) => handlePaymentStatusChange(e.target.value)}
                                            >
                                                <option value="">All Payment Statuses</option>
                                                <option value="Pending">Pending</option>
                                                <option value="Paid">Paid</option>
                                                <option value="Partially Paid">Partially Paid</option>
                                                <option value="Refunded">Refunded</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <button
                                                className="btn btn-primary float-end"
                                                onClick={() => fetchInvoices()}
                                            >
                                                <i className="ti ti-refresh me-2"></i>
                                                Refresh
                                            </button>
                                        </div>
                                    </div>

                                    <DataTable
                                        value={filteredInvoices}
                                        paginator
                                        rows={10}
                                        loading={loading}
                                        emptyMessage="No invoices found"
                                        sortMode="multiple"
                                        removableSort
                                        stripedRows
                                        tableStyle={{ minWidth: '70rem' }}
                                        rowsPerPageOptions={[5, 10, 20, 50]}
                                    >
                                        <Column
                                            field="reservationDetails.bookingReservationId"
                                            header="Booking Ref"
                                            sortable
                                            body={(rowData) => (
                                                <Button
                                                    label={rowData.reservationDetails?.bookingReservationId || "N/A"}
                                                    link
                                                    className="p-0 text-primary"
                                                    style={{ textDecoration: "none" }}
                                                    onClick={() => {
                                                        setSelectedInvoiceId(rowData._id);
                                                        setInvoiceDialogVisible(true);
                                                    }}
                                                />
                                            )}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column
                                            field="guestInfo.name"
                                            header="Guest"
                                            sortable
                                            style={{ minWidth: '150px' }}
                                        />
                                        <Column
                                            field="unitDetails.name"
                                            header="Apartment"
                                            sortable
                                            style={{ minWidth: '120px' }}
                                        />

                                        <Column
                                            header="Duration"
                                            body={durationTemplate}
                                            style={{ minWidth: '80px' }}
                                        />
                                        <Column
                                            field="totalAmount"
                                            header="Total Amount"
                                            sortable
                                            body={amountTemplate}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column
                                            header="Balance"
                                            body={balanceTemplate}
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column
                                            field="status"
                                            header="Status"
                                            body={statusTemplate}
                                            sortable
                                            style={{ minWidth: '100px' }}
                                        />
                                        <Column
                                            field="paymentStatus"
                                            header="Payment"
                                            body={paymentStatusTemplate}
                                            sortable
                                            style={{ minWidth: '120px' }}
                                        />
                                        <Column
                                            header="Actions"
                                            body={actionBodyTemplate}
                                            style={{ minWidth: '150px' }}
                                        />
                                    </DataTable>
                                </TabPanel>

                                <TabPanel header="Refunds">
                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <input
                                                className="form-control"
                                                placeholder="Search refunds..."
                                                value={refundSearchTerm}
                                                onChange={(e) => handleRefundSearch(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <select
                                                className="form-control"
                                                value={refundStatusFilter}
                                                onChange={(e) => handleRefundStatusChange(e.target.value)}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="Pending">Pending</option>
                                                <option value="Approved">Approved</option>
                                                <option value="Rejected">Rejected</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => fetchRefunds()}
                                            >
                                                <i className="ti ti-refresh me-2"></i>
                                                Refresh
                                            </button>
                                        </div>
                                    </div>

                                    <DataTable
                                        value={filteredRefunds}
                                        paginator
                                        rows={10}
                                        loading={loading}
                                        emptyMessage="No refunds found"
                                        sortMode="multiple"
                                        removableSort
                                        stripedRows
                                        tableStyle={{ minWidth: '60rem' }}
                                        rowsPerPageOptions={[5, 10, 20, 50]}
                                    >
                                        <Column
                                            field="bookingReservationId"
                                            header="Reservation #"
                                            sortable
                                            body={(rowData) => (
                                                <Button
                                                    label={rowData.bookingReservationId || "N/A"}
                                                    link
                                                    className="p-0 text-primary"
                                                    style={{ textDecoration: "none" }}
                                                    onClick={() => {
                                                        setSelectedInvoiceId(rowData.reservationId);
                                                        setInvoiceDialogVisible(true);
                                                    }}
                                                />
                                            )}
                                            style={{ minWidth: '140px' }}
                                        />
                                        <Column field="guestName" header="Guest" sortable style={{ minWidth: '140px' }} />
                                        <Column field="unitName" header="Apartment" sortable style={{ minWidth: '120px' }} />
                                        <Column header="Refund Amount" body={refundAmountTemplate} style={{ minWidth: '140px' }} />
                                        <Column field="refundStatus" header="Status" body={refundStatusTemplate} style={{ minWidth: '120px' }} />
                                        <Column field="refundRequestedAt" header="Requested" body={(rowData) => rowData.refundRequestedAt ? new Date(rowData.refundRequestedAt).toLocaleDateString() : 'N/A'} style={{ minWidth: '120px' }} />
                                        <Column field="refundApprovedAt" header="Approved" body={(rowData) => rowData.refundApprovedAt ? new Date(rowData.refundApprovedAt).toLocaleDateString() : '-'} style={{ minWidth: '120px' }} />
                                        <Column field="refundReference" header="Reference" style={{ minWidth: '140px' }} />
                                    </DataTable>
                                </TabPanel>
                            </TabView>
                        </div>
                    </div>
                </div>
            </div>

            {/* Invoice Details Dialog */}
            <BookingInvoiceDialog
                visible={invoiceDialogVisible}
                onHide={() => {
                    setInvoiceDialogVisible(false);
                    setSelectedInvoiceId(null);
                }}
                invoiceId={selectedInvoiceId}
            />

            {/* Record Payment Dialog */}
            <RecordPaymentDialog
                visible={paymentDialogVisible}
                onHide={() => {
                    setPaymentDialogVisible(false);
                    setSelectedInvoiceForPayment(null);
                }}
                invoice={selectedInvoiceForPayment}
                onPaymentSuccess={handlePaymentSuccess}
            />
        </Layout>
    );
};

export default BookingInvoices;
